import memoize from 'moize'

import arriereBackoffice, {
  AssetUploadInfo,
  BookKind,
  DateRangeInput,
  Granularity,
  Highlight,
  HomeBookSectionInput,
  HomeInvoiceType,
  HomeInvoiceWithUrl,
  HomeSorting,
  IDate,
  LeadSource,
  MergeAndAcquisitionIncluded,
  OnlineTravelAgency,
  PriceInput,
  PropertyBasedServicesSorting,
  RateType,
  ReviewPeriodKind,
  SeasonPriceInput,
  ServiceNameType,
  SortOrder,
  UpdateTaskManagementStatusArgs,
  UUID,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import type { PropertyRestrictionsInfoProps } from '@new/domains/bookings/stores/_types'
import type { TapeChartPropertiesFilterProps } from '@new/domains/bookingsTapeChart/BookingsTapeChart.types'
import { SaveBasePriceChangesMetadataArgs } from '@new/domains/finance/screens/PricingTool/types/pricingToolDashboard.types'
import type { CellData } from '@new/domains/properties/_types'
import type { Amenity } from '@new/domains/properties/screens/propertyAmenities/PropertyAmenities.types'
import type { UpdateCancellationPoliciesProps } from '@new/domains/properties/screens/propertyDistributionChannels/CancellationPolicies/CancellationPolicies.types'
import type {
  HomeCustomerExperienceIndicators,
  HomesDistributionsProps,
  SyncDistributionParams,
  UpdateHomeListingMutation,
} from '@new/domains/properties/screens/propertyDistributionChannels/DistributionChannels.types'
import { buildChannelTitlesPayload } from '@new/domains/properties/screens/propertyDistributionChannels/OtaDistributionChannel/constants'
import { UpdateCheckTime } from '@new/domains/properties/screens/propertyInfo/BasicInfo/BasicInfo.types'
import type {
  UpdateFinancialOffboardDate,
  UpdateLifecycle,
} from '@new/domains/properties/screens/propertyLifecycle/Lifecycle.type'
import type { CreateHomeImageProps } from '@new/domains/properties/screens/propertyPhotoGallery/PropertyPhotoGallery.types'
import { UpdateHomeDiscountSettingProps } from '@new/domains/properties/screens/propertyRevenueConfiguration/OTAPromotionsAndDiscounts/OTAPromotionsAndDiscounts.types'
import { SaveOwnerRestrictionFormProps } from '@new/domains/properties/screens/propertyRevenueConfiguration/OwnerRestrictions/OwnerRestrictions.types'
import type {
  BasePriceInput,
  PropertyRevenueConfigurationMutation,
} from '@new/domains/properties/screens/propertyRevenueConfiguration/RevenueConfiguration/RevenueConfiguration.types'
import type { GetPropertyReviewsProps } from '@new/domains/properties/screens/propertyReviews/PropertyReviews.types'
import { UpdateVendorRateBody } from '@new/domains/properties/screens/propertyServicesAndVendors/components/EditRecurringService/EditRecurringService.types'
import { VendorServiceBodyWithAmountRule } from '@new/domains/properties/screens/propertyServicesAndVendors/vendorRateAmountRule.types'

import { basicIndicatorInfo, indicatorInfo } from '@arriere/modules/fragments/indicators'
import { getHomesAirbnbTripIssues } from '@arriere/modules/propertiesList'
import { homeDistributionStatusFragment } from '@arriere/modules/propertySyncStatus'
import { npsRatingsOverviewFragment, ratingsOverviewFragment } from '@arriere/modules/regions'
import { settledDataOrThrow } from '@arriere/partialResponse'
import {
  HomeIndicatorParams,
  HomesWithCalendarInput,
  IBuyOutsMutationParams,
  IUpdateHomeParams,
  PaginationInput,
  PropertiesReviewsParams,
  UpdateHomeDataParams,
  UpdatePropertyHighlightProps,
  VerifyPropertyInfoProps,
} from '@arriere/types/Properties.types'
import { REVENUE_CONFIGURATION_SECTION_ID } from '@utils/customFields'

import { UnsetPriceInput } from '../types/PricingTapeChart.types'
import { customFieldsFragment } from './customFields'

const MAX_AGE_MEMOIZE = 60_000
const coHostRelationFragment = {
  coHostRelation: {
    type: true,
    account: {
      id: true,
      name: true,
    },
  },
}

export const getPropertyFamilyStatus = async (homeId: string) => {
  return arriereBackoffice.queries.home({
    __alias: 'getPropertyFamilyStatus',
    __args: { homeId },
    id: true,
    isAncestor: true,
    isSuccessor: true,
  })
}

export const getPropertyById = async (homeId: string) => {
  return arriereBackoffice.queries.home
    .settle({
      __alias: 'getPropertyById',
      __args: { homeId },
      id: true,
      area: true,
      bedroomsCount: true,
      bathroomsCount: true,
      checkTime: {
        checkOut: true,
      },
      description: true,
      halfBathroomsCount: true,
      hash: true,
      isAncestor: true,
      latitude: true,
      longitude: true,
      maxAdults: true,
      maxOccupancy: true,
      leases: {
        startDate: true,
        endDate: true,
      },
      maxPets: true,
      name: true,
      shortDescription: true,
      title: true,
      unitNumber: true,
      thumbnail: {
        url: true,
      },
      verifyItems: {
        item: true,
        verifiedAt: true,
        author: {
          name: true,
          kind: true,
        },
      },
      address: {
        address: true,
        city: true,
        cityId: true,
        countryCode: true,
        zipCode: true,
        state: {
          name: true,
        },
        region: { name: true, timezone: true },
        regionId: true,
        stateId: true,
      },
      channels: {
        title: true,
        source: true,
      },
      activeLeaseMonthlyAmount: true,
      customFields: {
        __args: { tags: undefined },
        ...customFieldsFragment,
      },
      links: {
        manual: true,
        listings: {
          leadSource: true,
          url: true,
          kind: true,
        },
      },
      rooms: {
        id: true,
        kind: true,
      },
      buyouts: {
        id: true,
        name: true,
        region: {
          name: true,
        },
      },
      blockedHomes: {
        id: true,
        name: true,
        region: {
          name: true,
        },
      },
      cancellationPolicies: {
        channelCancellationPolicyCategoryId: true,
        name: true,
        source: true,
        updatedAt: true,
        updatedBy: {
          name: true,
        },
      },
      assignedFieldOps: {
        vendorId: true,
        name: true,
      },
      lifecycle: {
        guestReadinessStatus: true,
      },
      permittingInfo: {
        additionalInspectionExpiration: true,
        businessLicense: true,
        businessLicenseExpiration: true,
        canGoLiveWithoutPermit: true,
        canOperateDuringRenewal: true,
        estimatedPermittingTimeline: true,
        exteriorInspectionExpiration: true,
        interiorInspectionExpiration: true,
        isLtrOnly: true,
        isPermitRequiredOnListing: true,
        jurisdictionAdditionalRestrictions: true,
        jurisdictionLtrMinNights: true,
        jurisdictionMaxStays: true,
        jurisdictionStrMinNights: true,
        listingRequirements: true,
        renewalPermitApplicationDate: true,
        strPermitExpiration: true,
        strPermitNumber: true,
        propertyAdditionalRestrictions: true,
        propertyMaxStays: true,
        totCertificateNumber: true,
        standardPermitApplicationDate: true,
        updatedAt: true,
        updatedBy: { name: true },
      },
      paymentPlans: { id: true, updatedAt: true, updatedBy: { name: true } },
    })
    .then(result => settledDataOrThrow({ result, queryName: 'getPropertyById' }))
}

export const getPropertyRegulationInfo = async (homeId: string) =>
  arriereBackoffice.queries.home
    .settle({
      __alias: 'getPropertyRegulationInfo',
      __args: { homeId },
      id: true,
      regulationInfo: {
        businessEntity: true,
        businessEntityPolicy: true,
        businessLicense: true,
        businessLicenseExpNotification: true,
        businessLicenseExpiration: true,
        coDor: true,
        companyBusinessLicense: true,
        companyCityBusinessLicense: true,
        dateOfLastPermitProcessUpdate: true,
        dateOfLastRentalInspection: true,
        firstRenewalActionNeeded: true,
        flDwe: true,
        hoa: true,
        hoaFeeOwner: true,
        homeownerInsuranceOnFile: true,
        inspectionRequiredAsPartOfPermit: true,
        insurance: true,
        insuranceExpiration: true,
        insuranceType: true,
        certificateOfInsurance: true,
        linkToPermitUrl: true,
        jurisdiction: true,
        lastActionTaken: true,
        permitName: true,
        permitNotes: true,
        permitReceivedDate: true,
        policyConcerns: true,
        risk: true,
        riskReasoning: true,
        strPermit: true,
        strPermitExpiryDate: true,
        strPermitNumber: true,
        zoning: true,
        updatedAt: true,
      },
    })
    .then(
      result =>
        settledDataOrThrow({ result, queryName: 'getPropertyRegulationInfo' }).regulationInfo,
    )

export const getPropertyAddress = async (homeId: string) => {
  return arriereBackoffice.queries.home({
    __alias: 'getPropertyAddress',
    __args: { homeId },
    address: {
      address: true,
      city: true,
      cityId: true,
      countryCode: true,
      zipCode: true,
      state: {
        name: true,
      },
      region: { name: true, timezone: true },
      regionId: true,
      stateId: true,
    },
  })
}

export const getPropertyByIdHeaderInfo = async (homeId: string) => {
  return arriereBackoffice.queries.home
    .settle({
      __alias: 'getPropertyByIdHeaderInfo',
      __args: { homeId },
      id: true,
      hash: true,
      name: true,
      title: true,
      isAncestor: true,
      revenueModel: true,
      customFields: {
        __args: { tags: undefined },
        section: {
          id: true,
        },
        fields: {
          id: true,
          value: true,
          name: true,
        },
      },
      details: {
        onBoardingStatus: true,
      },
      distributions: {
        __args: { activeOnly: false },
        agencyAccount: {
          id: true,
        },
      },
      links: {
        manual: true,
        listings: {
          leadSource: true,
          url: true,
          kind: true,
        },
      },
      ongoingBooking: {
        hash: true,
        guestName: true,
        detailedCheckIn: {
          effectiveTime: true,
        },
        detailedCheckOut: {
          effectiveTime: true,
        },
      },
      region: {
        id: true,
        name: true,
      },
      thumbnail: {
        url: true,
      },
      hotelId: true,
      lifecycle: {
        listingStatus: true,
        riskLevel: true,
        guestReadinessStatus: true,
        terminationNoticeDate: true,
      },
      permittingInfo: { status: true },
    })
    .then(result => settledDataOrThrow({ result, queryName: 'getPropertyByIdHeaderInfo' }))
}

export const getPropertyHealthTags = async (homeId: string) =>
  arriereBackoffice.queries.home
    .settle({
      __alias: 'getPropertyHealthTags',
      __args: { homeId },
      id: true,
      econPropertyFinancialHealth: true,
      rampingStatus: {
        isRamping: true,
      },
    })
    .then(result => settledDataOrThrow({ result, queryName: 'getPropertyHealthTags' }))

export const getPropertyByIdMinimalInfo = async (homeId: string) => {
  return arriereBackoffice.queries.home({
    __alias: 'getPropertyById',
    __args: { homeId },
    id: true,
    name: true,
    thumbnail: {
      url: true,
    },
    region: {
      name: true,
    },
    listings: {
      __args: { agency: OnlineTravelAgency.airbnb },
      active: true,
      externalHomeId: true,
    },
  })
}

export const getPropertyListings = async (propertyId: string) => {
  return arriereBackoffice.queries
    .home({
      __alias: 'getPropertyListings',
      __args: { homeId: propertyId },
      links: {
        listings: {
          kind: true,
          leadSource: true,
          url: true,
        },
      },
    })
    .then(response => response.links.listings)
}

export const getPropertyByIdLocationSummary = async (homeId: string) => {
  return arriereBackoffice.queries.home({
    __alias: 'getPropertyByIdLocationSummary',
    __args: { homeId },
    id: true,
    bathroomsCount: true,
    bedsCount: true,
    halfBathroomsCount: true,
    maxOccupancy: true,
    maxPets: true,
    name: true,
    revenueModel: true,
    hotelId: true,
    hotelRoomType: {
      hotelRoomTypeId: true,
    },
    customFields: {
      __args: { tags: undefined },
      section: {
        id: true,
      },
      fields: {
        id: true,
        value: true,
        name: true,
      },
    },
    details: {
      onBoardingStatus: true,
    },
    lifecycle: {
      listingStatus: true,
      riskLevel: true,
      guestReadinessStatus: true,
      terminationNoticeDate: true,
    },
    links: {
      manual: true,
      listings: {
        leadSource: true,
        url: true,
      },
    },
    region: {
      id: true,
      name: true,
    },
    rooms: {
      id: true,
      kind: true,
    },
    thumbnail: {
      url: true,
    },
  })
}

export const getPropertyByIdInTapeChart = async ({
  homeId,
  from,
  to,
}: {
  homeId?: string
  from?: IDate
  to?: IDate
}) => {
  return arriereBackoffice.queries.home({
    __alias: 'getPropertyByIdInTapeChart',
    __args: { homeId },
    id: true,
    title: true,
    shortDescription: true,
    maxAdults: true,
    bathroomsCount: true,
    bedsCount: true,
    halfBathroomsCount: true,
    maxOccupancy: true,
    hotelId: true,
    maxPets: true,
    name: true,
    revenueModel: true,
    hotelRoomType: {
      hotelRoomTypeId: true,
    },
    customFields: {
      __args: { tags: undefined },
      section: {
        id: true,
      },
      fields: {
        id: true,
        value: true,
        name: true,
      },
    },
    details: {
      onBoardingStatus: true,
    },
    lifecycle: {
      listingStatus: true,
      riskLevel: true,
      guestReadinessStatus: true,
      terminationNoticeDate: true,
    },
    links: {
      manual: true,
      listings: {
        leadSource: true,
        url: true,
        kind: true,
      },
    },
    region: {
      id: true,
      name: true,
    },
    rooms: {
      id: true,
      kind: true,
    },
    thumbnail: {
      url: true,
    },
    bookings: {
      __args: {
        leadSources: [],
        from,
        to,
      },
      results: {
        id: true,
        hash: true,
        source: true,
        detailedCheckIn: {
          effectiveTime: true,
        },
        detailedCheckOut: {
          effectiveTime: true,
        },
        guestName: true,
        guests: true,
        status: true,
      },
    },
    calendar: {
      __args: { from: from || '', to: to || '' },
      date: true,
      status: true,
      restrictions: {
        checkInAllowed: true,
        checkOutAllowed: true,
      },
    },
  })
}

export const getPropertyByIdTapeChartHeaderInfo = async ({ homeId }: { homeId?: string }) => {
  return arriereBackoffice.queries.home({
    __alias: 'getPropertyByIdTapeChartHeaderInfo',
    __args: { homeId },
    id: true,
    title: true,
    name: true,
    customFields: {
      __args: { tags: undefined },
      section: {
        id: true,
      },
      fields: {
        id: true,
        value: true,
        name: true,
      },
    },
    lifecycle: {
      listingStatus: true,
      riskLevel: true,
      guestReadinessStatus: true,
      terminationNoticeDate: true,
    },
    links: {
      manual: true,
      listings: {
        leadSource: true,
        url: true,
      },
    },
    region: {
      id: true,
      name: true,
    },
    rooms: {
      id: true,
      kind: true,
    },
    thumbnail: {
      url: true,
    },
  })
}

export const propertyInfoFieldsToVerify = () => {
  return arriereBackoffice.queries.verifyItems({
    __alias: 'getFieldsToVerify',
    name: true,
    required: true,
  })
}

export const getLifecycle = (homeId: string) => {
  return arriereBackoffice.queries.home({
    __alias: 'getLifecycle',
    __args: { homeId },
    lifecycle: {
      accountStatus: true,
      guestReadinessStatus: true,
      designStatus: true,
      listingStatus: true,
      closeDate: true,
      commencementDate: true,
      agreementStartDate: true,
      guestReadyDate: true,
      earlyListedDate: true,
      listedDate: true,
      listingNotes: true,
      operatingLaunchDate: true,
      uwOperationalLaunchDate: true,
      firstBookingDate: true,
      firstStayDate: true,
      terminationNoticeDate: true,
      finalRentableDate: true,
      terminationDate: true,
      riskLevel: true,
      riskReason: true,
      revenueModelChangeDate: true,
      financialOffboardDate: true,
    },
  })
}

export default {
  async getAllHighlights() {
    return arriereBackoffice.queries.highlights({
      __alias: 'getAllHighlights',
      id: true,
      name: true,
    })
  },
  async getPropertyByIdHighlights(homeId?: string) {
    return homeId === undefined
      ? undefined
      : arriereBackoffice.queries.home({
          __alias: 'getPropertyByIdHighlights',
          __args: { homeId },
          highlights: {
            highlightId: true,
            homeId: true,
            name: true,
            enabled: true,
            createdAt: true,
            listOrder: true,
            showOnSearch: true,
            shortDescription: true,
            assetId: true,
            coverImage: {
              id: true,
              context: true,
              s3Bucket: true,
              s3Key: true,
              public: true,
              createdAt: true,
              url: true,
              fileName: true,
            },
          },
        })
  },

  async getCancellationPolicyCategories() {
    return arriereBackoffice.queries.cancellationPolicyCategories({
      __alias: 'getCancellationPolicyCategories',
      id: true,
      description: true,
      name: true,
      source: true,
      items: {
        id: true,
        name: true,
        description: true,
      },
    })
  },

  async getTapeChartPropertyFilterCounters(
    tapeChartPropertiesFilter?: Omit<TapeChartPropertiesFilterProps, 'pagination'>,
  ) {
    return arriereBackoffice.queries.tapeChartCounters({
      __args: tapeChartPropertiesFilter ?? {},
      revenueModel: {
        name: true,
        value: true,
      },
      riskLevel: {
        name: true,
        value: true,
      },
    })
  },

  async getPropertySuccessors(homeId: string) {
    return arriereBackoffice.queries.successors({
      __args: { homeId },
    })
  },

  async getFinancialHealthDescription() {
    return arriereBackoffice.queries.econPropertyFinancialHealthDescription({
      color: true,
      description: true,
    })
  },

  async getHomeCustomFieldsForOTA(homeId: string, sectionId: string) {
    return arriereBackoffice.queries.home({
      __alias: 'getHomeCustomFieldsForOTA',
      __args: {
        homeId,
      },
      customFields: {
        __args: {
          sectionIds: [sectionId],
          withDisabled: true,
        },
        section: {
          id: true,
          name: true,
        },
        fields: {
          id: true,
          kind: true,
          name: true,
          value: true,
          tooltip: true,
          restriction: true,
        },
      },
    })
  },

  async getPropertyLeases(homeId: string) {
    return homeId === undefined
      ? undefined
      : arriereBackoffice.queries.home({
          __alias: 'getPropertyLeases',
          __args: {
            homeId,
          },
          leases: {
            startDate: true,
            endDate: true,
            amount: true,
            notes: true,
            signDate: true,
          },
        })
  },
  async getNoiseLevelDevicesPerProperty(homeId: string, pagination: PaginationInput) {
    return homeId === undefined
      ? undefined
      : arriereBackoffice.queries.home({
          __alias: 'getNoiseLevelDevicesPerProperty',
          __args: {
            homeId,
          },
          noiseLevelDevices: {
            __args: { pagination },
            results: {
              id: true,
              externalId: true,
              name: true,
              location: true,
              status: true,
              image: true,
              battery: true,
              updatedAt: true,
              reading: {
                threshold: true,
                noiseLevel: true,
                readAt: true,
              },
            },
          },
        })
  },

  async getOccupancyDevicesPerProperty(homeId: string, pagination: PaginationInput) {
    return homeId === undefined
      ? undefined
      : arriereBackoffice.queries.home({
          __alias: 'getOccupancyDevicesPerProperty',
          __args: {
            homeId,
          },
          maxOccupancy: true,
          occupancyDevices: {
            __args: { pagination },
            results: {
              id: true,
              name: true,
              externalId: true,
              threshold: true,
              updatedAt: true,
              reading: {
                occupancy: true,
                readAt: true,
              },
            },
          },
        })
  },
  async getHomeSummaryIndicators({
    homeId,
    fromDate,
    toDate,
    granularity,
    pagination,
  }: HomeIndicatorParams) {
    return arriereBackoffice.queries.homes({
      __alias: 'getHomeSummaryIndicators',
      __args: { homeIds: [homeId], pagination },
      results: {
        id: true,
        indicators: {
          __args: { granularity },
          finance: {
            adr: {
              __args: { from: fromDate, to: toDate },
              period: true,
            },
            bookingVolume: {
              __args: {
                from: fromDate,
                to: toDate,
                mergeAndAcqIncluded: MergeAndAcquisitionIncluded.withoutMa,
              },
              aggregatedValue: true,
              annualizedValue: true,
            },
          },
          sales: {
            occupancyRate: {
              __args: { from: fromDate, to: toDate },
              values: {
                value: true,
              },
              aggregatedValue: true,
            },
          },
        },
      },
    })
  },
  async getHomeIndicators({
    homeId,
    fromDate,
    toDate,
    granularity,
    pagination,
  }: HomeIndicatorParams) {
    return arriereBackoffice.queries.homes({
      __alias: 'getHomeIndicators',
      __args: { homeIds: [homeId], pagination },
      results: {
        indicators: {
          __args: { granularity },
          customerExperience: {
            airbnbSuperhostRating: {},
            airbnbFiveStarReviewsToSuperhost: {},
            airbnbSuperhostPeriod: {
              end: true,
              start: true,
            },
            nps: {},
          },
          finance: {
            adr: {
              __args: { from: fromDate, to: toDate },
              period: true,
            },
            revpau: {
              __args: { from: fromDate, to: toDate },
              period: true,
              weekdays: true,
              weekends: true,
            },
            bookingVolume: {
              __args: {
                from: fromDate,
                to: toDate,
                mergeAndAcqIncluded: MergeAndAcquisitionIncluded.withoutMa,
              },
              ...indicatorInfo,
              annualizedValue: true,
            },
            realizedRevenue: {
              __args: { from: fromDate, to: toDate },
              ...indicatorInfo,
              annualizedValue: true,
            },
            channelDistribution: {
              __args: {
                from: fromDate,
                to: toDate,
                mergeAndAcqIncluded: MergeAndAcquisitionIncluded.all,
              },
              channelName: true,
              percentage: true,
              total: true,
            },
          },
          sales: {
            averageBookingLength: {
              __args: { from: fromDate, to: toDate },
              ...indicatorInfo,
            },
            medianBookingLeadTime: {
              __args: { from: fromDate, to: toDate },
              ...indicatorInfo,
            },
            occupancyRate: {
              __args: { from: fromDate, to: toDate },
              ...indicatorInfo,
            },
            weekendOccupancyRate: {
              __args: { from: fromDate, to: toDate },
              ...indicatorInfo,
            },
            averageGroupSize: {
              __args: { from: fromDate, to: toDate },
              ...basicIndicatorInfo,
            },
            cancelledBookings: {
              __args: { from: fromDate, to: toDate },
              ...basicIndicatorInfo,
            },
            returningGuests: {
              __args: { from: fromDate, to: toDate },
              ...basicIndicatorInfo,
            },
            bookings: {
              __args: { from: fromDate, to: toDate },
              ...basicIndicatorInfo,
            },
            channelDistribution: {
              __args: {
                from: fromDate,
                to: toDate,
                mergeAndAcqIncluded: MergeAndAcquisitionIncluded.all,
              },
              channelName: true,
              percentage: true,
              total: true,
            },
            repeats: {
              __args: {
                from: fromDate,
                to: toDate,
              },
            },
          },
        },
      },
    })
  },
  async getHomeComparativeIndicators({
    homeId,
    fromDate,
    toDate,
    granularity,
    pagination,
  }: HomeIndicatorParams) {
    return arriereBackoffice.queries.homes({
      __alias: 'getHomeComparativeIndicators',
      __args: { homeIds: [homeId], pagination },
      results: {
        indicators: {
          __args: { granularity },
          finance: {
            bookingVolume: {
              __args: {
                from: fromDate,
                to: toDate,
                mergeAndAcqIncluded: MergeAndAcquisitionIncluded.all,
              },
              ...indicatorInfo,
              annualizedValue: true,
            },
            realizedRevenue: {
              __args: {
                from: fromDate,
                to: toDate,
                mergeAndAcqIncluded: MergeAndAcquisitionIncluded.all,
              },
              ...indicatorInfo,
              annualizedValue: true,
            },
          },
          sales: {
            averageBookingLength: {
              __args: { from: fromDate, to: toDate },
              ...indicatorInfo,
            },
            medianBookingLeadTime: {
              __args: { from: fromDate, to: toDate },
              ...indicatorInfo,
            },
            occupancyRate: {
              __args: { from: fromDate, to: toDate },
              ...indicatorInfo,
            },
            weekendOccupancyRate: {
              __args: { from: fromDate, to: toDate },
              ...indicatorInfo,
            },
            averageGroupSize: {
              __args: { from: fromDate, to: toDate },
              ...basicIndicatorInfo,
            },
            cancelledBookings: {
              __args: { from: fromDate, to: toDate },
              ...basicIndicatorInfo,
            },
            returningGuests: {
              __args: { from: fromDate, to: toDate },
              ...basicIndicatorInfo,
            },
            bookings: {
              __args: { from: fromDate, to: toDate },
              ...basicIndicatorInfo,
            },
            channelDistribution: {
              __args: {
                from: fromDate,
                to: toDate,
                mergeAndAcqIncluded: MergeAndAcquisitionIncluded.all,
              },
              channelName: true,
              percentage: true,
              total: true,
            },
          },
        },
      },
    })
  },
  getFutureRevenueByHome({ from, to, homeId }: { from: IDate; to: IDate; homeId: string }) {
    return arriereBackoffice.queries.home({
      __alias: 'getFutureRevenueByHome',
      __args: { homeId },
      indicators: {
        __args: {
          granularity: Granularity.monthly,
        },
        finance: {
          futureRevenue: {
            __args: { from, to, mergeAndAcqIncluded: MergeAndAcquisitionIncluded.all },
            date: true,
            booked: true,
            paid: true,
          },
        },
      },
    })
  },

  async getPropertyBookSections(homeId: string, kind: BookKind) {
    return arriereBackoffice.queries.home({
      __alias: 'getPropertyBookSections',
      __args: { homeId },
      region: {
        id: true,
      },
      book: {
        __args: { kind },
        sections: {
          homeId: true,
          defaultValue: true,
          value: true,
          active: true,
          title: true,
          listOrder: true,
          key: true,
          bookKind: true,
        },
      },
    })
  },

  async getAllProperties(pagination: PaginationInput) {
    return arriereBackoffice.queries.homes({
      __alias: 'getAllProperties',
      __args: {
        pagination,
      },
      results: {
        id: true,
        name: true,
        region: {
          name: true,
          id: true,
        },
      },
    })
  },

  async getHomeNames(homeIds: UUID[]) {
    return arriereBackoffice.queries
      .homes({
        __alias: 'getHomeNames',
        __args: {
          pagination: {
            page: 1,
            size: homeIds.length,
          },
          homeIds,
        },
        results: {
          name: true,
          title: true,
        },
      })
      .then(({ results }) => results.map(home => home.title))
  },

  async getPropertiesFloorRates(homeIds: string[]) {
    return arriereBackoffice.queries.floorRates({
      __args: {
        homeIds,
      },
      homeId: true,
      rate: true,
      rateByMonth: true,
    })
  },
  async getAllPropertiesForTapeChart({
    acquisitionChannel,
    amenityIds,
    checkIn,
    checkOut,
    exactBedrooms,
    highlightIds,
    homeIds,
    licensingFramework,
    maxPrice,
    minBathrooms,
    minBedrooms,
    minPets,
    minPrice,
    name,
    pagination,
    petPolicy,
    regionIds,
    revenueModel,
    riskLevel,
    seasonGroupIds,
    targetPartner,
    withOffboarded,
    minOccupancyRate,
    maxOccupancyRate,
    occupancyPeriod,
  }: TapeChartPropertiesFilterProps) {
    return arriereBackoffice.queries.tapeChartPaginatedHomes.raw({
      __alias: 'tapeChartPaginatedHomes',
      __args: {
        regionIds,
        homeIds,
        name,
        checkIn,
        checkOut,
        minPrice,
        maxPrice,
        petPolicy,
        minBathrooms,
        minBedrooms,
        amenityIds,
        highlights: highlightIds,
        riskLevel,
        revenueModel,
        pagination,
        seasonGroupIds,
        licensingFramework,
        targetPartner,
        acquisitionChannel,
        minPets,
        bedrooms: exactBedrooms,
        withOffboarded,
        maxOccupancyRate,
        minOccupancyRate,
        occupancyPeriod,
        sortBy: HomeSorting.regionName,
      },
      pages: true,
      paginationParams: {
        page: true,
      },
      results: {
        isIntegratedPricingEnabled: true,
        id: true,
        name: true,
        region: {
          id: true,
          name: true,
        },
        guestReadinessStatus: true,
        lifecycle: {
          offboarded: true,
          riskLevel: true,
        },
        details: {
          maintenanceStatus: true,
          publishHighRisk: true,
          onBoardingStatus: true,
        },
        home: {
          hasPool: true,
          roomsCount: true,
          bedroomsCount: true,
        },
      },
    })
  },
  async propertyBlockedPeriods({
    from,
    to,
    homeIds,
  }: {
    from: IDate
    to: IDate
    homeIds: string[]
  }) {
    return arriereBackoffice.queries
      .tapeChart({
        __alias: 'getPropertyBlockedPeriods',
        __args: {
          homeIds,
          period: { start: from, end: to },
        },
        searchAvailabilityBlocks: {
          __args: {},
          bookingId: true,
          homeId: true,
          id: true,
          notificationSetting: {
            notifyAuthor: true,
            notifyRelocationTeam: true,
            notifyFieldOpsTeam: true,
          },
          parent: {
            id: true,
            source: true,
            homeId: true,
            bookingId: true,
            createdBy: true,
            createdAt: true,
            updatedAt: true,
            updatedBy: true,
            note: true,
            period: {
              start: true,
              end: true,
            },
          },
          period: {
            start: true,
            end: true,
          },
          source: true,
          createdBy: true,
          createdAt: true,
          updatedAt: true,
          updatedBy: true,
          note: true,
        },
      })
      .then(data => data.searchAvailabilityBlocks)
  },
  async propertyDatedPrices({ from, to, homeIds }: { from: IDate; to: IDate; homeIds: string[] }) {
    return arriereBackoffice.queries
      .tapeChart({
        __alias: 'getPropertyDatedPrices',
        __args: {
          period: { start: from, end: to },
          homeIds,
        },
        searchHomeDatedPrices: {
          date: true,
          homeId: true,
          maxStay: true,
          minStay: true,
          rate: true,
        },
      })
      .then(results => results.searchHomeDatedPrices)
  },
  async getPropertiesDatedPrices({
    from,
    to,
    homeIds,
    weekdays,
    rateType,
  }: {
    from: IDate
    to: IDate
    homeIds: string[]
    weekdays: number[]
    rateType: RateType
  }): Promise<CellData[]> {
    return arriereBackoffice.queries
      .pricingTapeChart({
        __alias: 'getPropertiesDatedPrices',
        __args: {
          rateType,
        },
        searchHomeDatedPrices: {
          __args: { period: { start: from, end: to }, homeIds, dayOfWeek: weekdays },
          date: true,
          homeId: true,
          minStay: true,
          rate: true,
          checkinAllowed: true,
          checkoutAllowed: true,
          canEdit: true,
          hasHistory: true,
          rateType: true,
          occupancyType: true,
          hasConditionsHistory: true,
          ownerRestrictions: {
            floorRate: true,
            maxNights: true,
            minNights: true,
          },
        },
      })
      .then(results => results.searchHomeDatedPrices)
  },

  async getPricingTapeChartCellHistory({
    date,
    homeId,
    rateType,
  }: {
    date: IDate
    homeId: string
    rateType: RateType
  }) {
    return arriereBackoffice.queries
      .pricingTapeChart({
        __args: { rateType },
        searchHomeDatedPriceChangeHistory: {
          __args: { homeId, date },
          homeId: true,
          date: true,
          previousValue: true,
          newValue: true,
          author: { kind: true, name: true },
          message: true,
          updatedAt: true,
          previousConditions: {
            minStay: true,
            maxStay: true,
            checkinAllowed: true,
            checkoutAllowed: true,
          },
          newConditions: {
            minStay: true,
            maxStay: true,
            checkinAllowed: true,
            checkoutAllowed: true,
          },
        },
      })
      .then(results => results.searchHomeDatedPriceChangeHistory)
  },

  async updatePropertiesDatedPricesAndRestrictions(
    values: PriceInput[],
    message: string,
    rateType: RateType,
  ): Promise<string | any> {
    return arriereBackoffice.mutations
      .pricingTapeChart({
        __args: {
          rateType,
        },
        updateDatedValues: {
          __args: {
            values,
            message,
          },
        },
      })
      .then(result => result?.updateDatedValues)
  },

  async unsetDatedValues(
    values: UnsetPriceInput[],
    message: string,
    rateType: RateType,
  ): Promise<string | undefined> {
    return arriereBackoffice.mutations
      .pricingTapeChart({
        __args: {
          rateType,
        },
        unsetDatedValues: {
          __args: {
            values,
            message,
          },
        },
      })
      .then(result => result?.unsetDatedValues)
  },

  async updateSeasonPricing(values: SeasonPriceInput[], message: string): Promise<string | any> {
    return arriereBackoffice.mutations
      .seasonPrices({
        update: {
          __args: {
            values,
            message,
          },
        },
      })
      .then(result => result?.update)
  },

  async getHomeInvoices(homeId: string): Promise<HomeInvoiceWithUrl[]> {
    return arriereBackoffice.queries.invoices({
      __args: {
        homeId,
      },
      info: {
        assetId: true,
        fileName: true,
        homeId: true,
        period: {
          start: true,
          end: true,
        },
        invoiceType: true,
        createdAt: true,
        updatedAt: true,
      },
      url: true,
    })
  },
  async getHighlightsList(): Promise<Pick<Highlight, 'id' | 'name'>[]> {
    return arriereBackoffice.queries.highlights({
      name: true,
      id: true,
    })
  },
  async getFilterableAmenitiesList(): Promise<Pick<Amenity, 'id' | 'name'>[]> {
    return arriereBackoffice.queries.filterableAmenities({
      name: true,
      id: true,
    })
  },
  async getPropertyReviews({
    homeId,
    page,
    sources,
    paginationSize,
    from,
    to,
    terms,
    minRating,
    maxRating,
    categories,
  }: GetPropertyReviewsProps) {
    return arriereBackoffice.queries
      .home({
        __alias: 'getPropertyReviews',
        __args: {
          homeId,
        },
        reviewsByBookingSources: {
          __args: {
            pagination: { page, size: paginationSize },
            sources,
            periodKind: ReviewPeriodKind.reviewCreationDate,
            period: {
              start: from,
              end: to,
            },
            terms,
            minRating,
            maxRating,
            categories,
          },
          total: true,
          pages: true,
          results: {
            bookingId: true,
            bookingHash: true,
            source: true,
            guestName: true,
            period: {
              start: true,
              end: true,
            },
            reviews: {
              id: true,
              source: true,
              privateFeedback: true,
              response: true,
              externalReviewId: true,
              createdAt: true,
              bookingPeriod: {
                start: true,
                end: true,
              },
              categoryReviews: {
                category: true,
                rating: true,
                comment: true,
                tags: true,
              },
              booking: {
                guestId: true,
                guestName: true,
                hash: true,
                home: { name: true },
                guestRate: {
                  id: true,
                  categories: {
                    category: true,
                    comment: true,
                    rating: true,
                    tags: true,
                  },
                  createdAt: true,
                  privateFeedback: true,
                  publicReview: true,
                  response: true,
                },
              },
            },
          },
        },
      })
      .then(results => results.reviewsByBookingSources)
  },
  async getPropertyRatings(homeId: string) {
    return arriereBackoffice.queries
      .home({
        __alias: 'getPropertyRatings',
        __args: {
          homeId,
        },
        ratings: {
          homeAway: {
            overall: {
              avgStars: true,
              totalRates: true,
            },
          },
          airBnb: {
            overall: {
              avgStars: true,
              totalRates: true,
            },
            accuracy: {
              avgStars: true,
              totalRates: true,
            },
            checkIn: {
              avgStars: true,
              totalRates: true,
            },
            communication: {
              avgStars: true,
              totalRates: true,
            },
            cleanliness: {
              avgStars: true,
              totalRates: true,
            },
            location: {
              avgStars: true,
              totalRates: true,
            },
            value: {
              avgStars: true,
              totalRates: true,
            },
          },
        },
      })
      .then(results => results.ratings)
  },

  async getHomesDistributions(homeId: string) {
    return arriereBackoffice.queries.home({
      __alias: 'getHomesDistributions',
      __args: {
        homeId,
      },
      distributions: {
        __args: {
          activeOnly: false,
        },
        active: true,
        homeTitle: true,
        externalHomeId: true,
        homeId: true,
        agencyAccount: {
          agency: true,
          market: true,
          notes: true,
          channel: true,
        },
        status: true,
        statuses: {
          ...homeDistributionStatusFragment(OnlineTravelAgency.airbnb),
        },
        lastUpdateAt: true,
      },
      listings: {
        active: true,
        agencyAccount: {
          id: true,
          agency: true,
        },
        ...coHostRelationFragment,
      },
    })
  },

  async getHomesDistributionsAndListings({ homeId, ota }: HomesDistributionsProps) {
    return arriereBackoffice.queries.home({
      __alias: 'getHomesDistributionsAndListings',
      __args: {
        homeId,
      },
      distributions: {
        __args: {
          activeOnly: false,
          agency: ota,
        },
        active: true,
        agencyAccount: {
          agency: true,
          market: true,
          notes: true,
          channel: true,
        },
        status: true,
        statuses: {
          ...homeDistributionStatusFragment(ota),
        },
        lastUpdateAt: true,
      },
      listings: {
        __args: { agency: ota },
        active: true,
        agencyAccount: {
          agency: true,
        },
        ...coHostRelationFragment,
        propertyType: true,
        propertyTypeGroup: true,
      },
    })
  },
  async getHomeQualityStandardsState(homeId: string) {
    return arriereBackoffice.queries.airbnbDistribution
      .raw({
        __alias: 'getHomeQualityStandardsState',
        tripIssuesSearch: {
          __args: { homeIds: [homeId], regionIds: [] },
          total: true,
          pages: true,
          results: {
            qualityStandardsState: true,
            qualityStandardsStateDate: true,
            stats: {
              bookingIssuesCount: true,
              bookingIssuesRatio: true,
            },
          },
        },
      })
      .then(res => res.data?.tripIssuesSearch?.results[0])
  },
  async getHomesList(searchTerm: string) {
    return arriereBackoffice.queries.homes
      .settle({
        __alias: 'getHomesList',
        __args: {
          pagination: { page: 1, size: 30 },
          name: searchTerm.toLocaleLowerCase().trim(),
        },
        results: {
          id: true,
          name: true,
          title: true,
          thumbnail: {
            url: true,
          },
          region: {
            name: true,
          },
          isAncestor: true,
          isSuccessor: true,
          revenueModel: true,
        },
      })
      .then(result => settledDataOrThrow({ result, queryName: 'getHomesList' }))
  },

  async getHomeDistributionListingInfo({ homeId, ota }: HomesDistributionsProps) {
    return arriereBackoffice.queries
      .distribution({
        __alias: 'getHomeDistributionListingInfo',
        getHomeListing: {
          __args: { homeId, agency: ota },
          homeId: true,
          source: true,
          title: true,
          externalHomeId: true,
          externalListingId: true,
          description: true,
          createdAt: true,
          updatedAt: true,
          access: true,
          houseRules: true,
          interaction: true,
          neighborhood: true,
          notes: true,
          space: true,
          transit: true,
          distributionSettings: {
            commonChannelSettings: {
              defaultPrice: true,
              weeklyDiscount: true,
              monthlyDiscount: true,
              latitude: true,
              longitude: true,
              hasInternet: true,
              hasWheelchairAccess: true,
              hasBedInCommonSpace: true,
              eventsAllowed: true,
            },
            airbnbSettings: {
              headline: true,
              propertyType: true,
              listingType: true,
              checkInOptionCategory: true,
              checkInOptionInstruction: true,
              synchronizationCategory: true,
              cancellationPolicy: true,
              cancellationNonRefundablePricePercent: true,
              earlyBirdDiscount: true,
              earlyBirdDiscountDays: true,
              earlyBirdDiscountPercent: true,
              lastMinuteBirdDiscount: true,
              lastMinuteBirdDiscountDays: true,
              lastMinuteBirdDiscountPercent: true,
              propertyTypeGroup: true,
              securityDeposit: true,
              permitId: true,
              leadTime: true,
              useUnitLeadTime: true,
              directions: true,
              roomNumber: true,
              manual: true,
            },
          },
          active: true,
          apiConnected: true,
          summary: true,
          channels: {
            active: true,
            ota: true,
            title: true,
          },
          agencyAccount: {
            agency: true,
            channel: true,
            channelAccountId: true,
            email: true,
            id: true,
            market: true,
            notes: true,
          },
          listingSummary: {
            lockedAttributes: true,
          },
          ...coHostRelationFragment,
        },
      })
      .then(result => {
        return result.getHomeListing
      })
  },

  // Mutations

  async updateCheckTime({ homeId, checkOutTime }: UpdateCheckTime) {
    return arriereBackoffice.mutations.home({
      updateCheckTime: {
        __args: {
          homeId,
          checkOutTime,
        },
      },
    })
  },

  async updatePropertyBookSections(sections: HomeBookSectionInput[]) {
    return arriereBackoffice.mutations.homeBooks({
      bindSections: { __args: { sections } },
    })
  },

  async createHomeImage({
    homeId,
    extension,
    size,
    fileName,
    width,
    height,
  }: CreateHomeImageProps) {
    return arriereBackoffice.mutations.createHomeImage({
      __args: {
        homeId,
        extension,
        size,
        fileName,
        width,
        height,
      },
      assetId: true,
      url: true,
      externalUrl: true,
    })
  },

  async updateHomeCustomFields({
    homeId,
    customFields,
    verifyCustomFields = false,
    verifyAllCustomFieldsUsed,
  }: IUpdateHomeParams) {
    return arriereBackoffice.mutations.updateHome({
      __alias: 'updateHomeCustomFields',
      __args: {
        homeId,
        customFields,
        verifyCustomFields,
        verifyAllCustomFieldsUsed,
      },
    })
  },
  async addBuyouts({ homeId, buyoutIds }: IBuyOutsMutationParams) {
    return arriereBackoffice.mutations.addBuyouts({
      __args: {
        homeId,
        buyoutIds,
      },
    })
  },
  async deleteBuyouts({ homeId, buyoutIds }: IBuyOutsMutationParams) {
    return arriereBackoffice.mutations.deleteBuyouts({
      __args: {
        homeId,
        buyoutIds,
      },
    })
  },

  async createHomeInvoiceAsset({
    homeId,
    size,
    fileName,
  }: {
    homeId: string
    size?: number
    fileName?: string
  }): Promise<AssetUploadInfo> {
    return arriereBackoffice.mutations.createHomeInvoice({
      __args: {
        homeId,
        size,
        fileName,
      },
      assetId: true,
      url: true,
      externalUrl: true,
    })
  },

  async deleteHomeInvoice(assetId: string): Promise<boolean> {
    return arriereBackoffice.mutations.deleteHomeInvoice({
      __args: { assetId },
    })
  },

  async updateHomeInvoice({
    homeId,
    assetId,
    fileName,
    period,
    invoiceType,
  }: {
    homeId: string
    assetId: string
    fileName: string
    period: DateRangeInput
    invoiceType: HomeInvoiceType
  }): Promise<boolean> {
    return arriereBackoffice.mutations.updateHomeInvoice({
      __args: { homeId, assetId, fileName, period, invoiceType },
    })
  },
  async updateHomeListing({ agency, homeId, formFields }: UpdateHomeListingMutation) {
    const {
      bookingComTitle: _bookingComTitle,
      expediaTitle: _expediaTitle,
      marriottTitle: _marriottTitle,
      ...restFormFields
    } = formFields
    const channelTitles =
      agency === OnlineTravelAgency.rentalsUnited
        ? buildChannelTitlesPayload(formFields)
        : undefined
    return arriereBackoffice.mutations.distribution({
      updateListing: {
        __args: {
          agency,
          listingId: homeId,
          ...restFormFields,
          ...(channelTitles ? { channelTitles } : {}),
        },
      },
    })
  },

  // REVENUE CONFIGURATION

  async getPropertyRevenueConfiguration(id: string) {
    return arriereBackoffice.queries.revenueProperty({
      __args: { id },
      id: true,
      seasonGroup: {
        id: true,
        name: true,
      },
      yieldingGroup: {
        id: true,
        name: true,
      },
      yieldingMap: {
        id: true,
        name: true,
      },
      nightlyMinimumFloor: true,
      floorRateByMonth: true,
      pricingSourceType: true,
      weeklyDiscount: true,
      monthlyDiscount: true,
      isDiscountsByMonthConfigured: true,
    })
  },

  async getPropertyRevenueDiscounts(id: string) {
    return arriereBackoffice.queries.revenueProperty({
      __args: { id },
      weeklyDiscount: true,
      monthlyDiscount: true,
    })
  },

  async setPropertyRevenueConfiguration({
    id,
    yieldingMapId,
    yieldingGroupId,
    seasonGroupId,
    message,
    nightlyMinimumFloor,
    floorRateByMonth,
    pricingSource,
    weeklyDiscount,
    monthlyDiscount,
  }: PropertyRevenueConfigurationMutation) {
    return arriereBackoffice.mutations
      .propertyMutations({
        __alias: 'setPropertyRevenueConfigurationValues',
        update: {
          __args: {
            id,
            yieldingMapId,
            yieldingGroupId,
            seasonGroupId,
            message,
            nightlyMinimumFloor,
            floorRateByMonth,
            pricingSource,
            weeklyDiscount,
            monthlyDiscount,
          },
          id: true,
        },
      })
      .then(response => !!response?.update?.id)
  },

  async updateTaskManagementStatus({
    userId,
    userEmail,
    userName,
    marketId,
    propertyId,
    comment,
    analystSentiment,
    tagsSelected,
  }: UpdateTaskManagementStatusArgs) {
    return arriereBackoffice.mutations
      .updateTaskManagementStatus({
        __alias: 'updateTaskManagementStatus',
        __args: {
          userId,
          userEmail,
          marketId,
          propertyId,
          comment,
          analystSentiment,
          tagsSelected,
          userName,
        },
        success: true,
      })
      .then(response => !!response?.success)
  },

  async updateWheelhouseBasePrice(prices: BasePriceInput[]) {
    return arriereBackoffice.mutations
      .propertyMutations({
        __alias: 'updateWheelhouseBasePrices',
        updateWheelhouseBasePrices: {
          __args: {
            prices,
          },
        },
      })
      .then(response => !!response?.updateWheelhouseBasePrices)
  },

  async saveBasePriceChangesMetadata({
    userId,
    userEmail,
    marketId,
    propertyId,
    percentChange,
    previousBasePrice,
    newBasePrice,
    comment,
    tagsSelected,
    analystSentiment,
    userName,
  }: SaveBasePriceChangesMetadataArgs) {
    return arriereBackoffice.mutations
      .saveBasePriceChangesMetadata({
        __alias: 'saveBasePriceChangesMetadata',
        __args: {
          userId,
          userEmail,
          marketId,
          propertyId,
          percentChange,
          previousBasePrice,
          newBasePrice,
          comment,
          tagsSelected,
          analystSentiment,
          userName,
        },
        message: true,
        success: true,
      })
      .then(response => response)
  },

  async getRevenueConfigurationTableData(
    pagination?: PaginationInput,
    name?: string,
    regionIds?: string[],
  ) {
    return arriereBackoffice.queries
      .revenueProperties({
        __alias: 'revenuePropertiesConfigurationData',
        __args: { pagination, name, regionIds },
        total: true,
        paginationParams: { size: true, page: true },
        pages: true,
        results: {
          id: true,
          name: true,
          regionId: true,
          regionName: true,
          yieldingMap: { name: true, id: true },
          yieldingGroup: { name: true, id: true },
          seasonGroup: { name: true, id: true },
          floorRate: true,
          weeklyDiscount: true,
          monthlyDiscount: true,
        },
      })
      .then(response => response?.results || [])
  },

  async updateLifecycle(lifecycleProps: UpdateLifecycle) {
    return arriereBackoffice.mutations.updateLifecycleFields({
      __args: lifecycleProps,
    })
  },

  async updateFinancialOffboardDate(financialOffboardDateProps: UpdateFinancialOffboardDate) {
    return arriereBackoffice.mutations.updateFinancialOffboardDate({
      __alias: 'updateFinancialOffboardDate',
      __args: financialOffboardDateProps,
    })
  },

  async updateHomeCancellationPolicies({ homeId, policies }: UpdateCancellationPoliciesProps) {
    return arriereBackoffice.mutations.home({
      updateHomeCancellationPolicy: {
        __args: { homeId, policies },
      },
    })
  },

  async getPropertyRestrictionsById({ homeId, period }: PropertyRestrictionsInfoProps) {
    return arriereBackoffice.queries.home({
      __alias: 'getPropertyRestrictionsById',
      __args: { homeId },
      maxOccupancy: true,
      petPolicy: true,
      maxAdults: true,
      maxPets: true,
      calendar: {
        __args: { from: period.from, to: period.to },
        restrictions: {
          checkInAllowed: true,
          checkOutAllowed: true,
        },
      },
      customFields: {
        __args: {
          sectionIds: [REVENUE_CONFIGURATION_SECTION_ID],
        },
        fields: {
          id: true,
          name: true,
          value: true,
        },
      },
      region: {
        timezone: true,
      },
      checkTime: {
        checkIn: true,
      },
    })
  },

  async getPropertyStayLimit(homeId: string, from: IDate, to: IDate) {
    return arriereBackoffice.queries.home.memo({
      __alias: 'getPropertyStayLimitByDate',
      __args: { homeId },
      calendar: {
        __args: { from, to },
        date: true,
        minStay: true,
        maxStay: true,
      },
    })
  },

  async calendarDatePricesAvailability({
    from,
    to,
    homeId,
  }: {
    from: IDate
    to: IDate
    homeId: string
  }) {
    return arriereBackoffice.queries.home
      .memo({
        __alias: 'getCalendarDatePricesAvailability',
        __args: {
          homeId,
        },
        calendar: {
          __args: { from, to },
          date: true,
          minStay: true,
          rate: true,
          status: true,
          restrictions: {
            checkInAllowed: true,
            checkOutAllowed: true,
          },
        },
      })
      .then(results => results.calendar)
  },

  async updatePropertyHighlights({ homeId, homeHighlights }: UpdatePropertyHighlightProps) {
    return arriereBackoffice.mutations.home({
      updateHomeHighlights: {
        __args: {
          homeId,
          homeHighlights,
        },
      },
    })
  },
  async getRateDetails(rateId: string) {
    return arriereBackoffice.queries
      .vendorServices({
        __alias: 'findVendorRate',
        findVendorRate: {
          __args: { id: rateId },
          id: true,
          homeId: true,
          vendorId: true,
          serviceType: true,
          serviceName: true,
          rateInCents: true,
          ratePeriod: true,
          dateOfGivenRate: true,
          startDate: true,
          daysOfService: true,
          serviceFrequency: true,
          billingFrequency: true,
          ownerPayAmountInCents: true,
          ownerPayPeriod: true,
          notes: true,
          autoAssign: true,
          effectiveRateInCents: true,
          amountRule: {
            baseServiceName: true,
            percentage: true,
            minimumRateInCents: true,
          },
          dependentCalculatedRates: {
            vendorRateId: true,
            serviceName: true,
            percentage: true,
            minimumRateInCents: true,
            effectiveRateInCents: true,
          },
        },
      })
      .then(rateDetails => {
        if (rateDetails?.findVendorRate?.id) {
          return arriereBackoffice.queries
            .vendors({
              __alias: 'findVendorSimplified',
              findVendor: {
                __args: { vendorId: rateDetails.findVendorRate.vendorId },
                name: true,
                active: true,
                salesforceVendorId: true,
              },
            })
            .then(vendorDetails => ({
              ...vendorDetails?.findVendor,
              ...rateDetails?.findVendorRate,
            }))
        } else {
          Alert.Error('Sorry, there is no vendor info related to this rate')
          return undefined
        }
      })
  },
  async getVendor(vendorId: string, homeId: string, availabilityRange: DateRangeInput) {
    return arriereBackoffice.queries
      .vendors({
        __alias: 'findVendor',
        findVendor: {
          __args: { vendorId },
          regionIds: true,
          documentation: {
            coiExpirationDate: true,
            passedBackgroundCheck: true,
            specializedLicenseAndCerts: {
              name: true,
              type: true,
              fileIds: true,
            },
          },
          coiFile: {
            id: true,
            fileName: true,
            url: true,
          },
          w9w8File: {
            id: true,
            fileName: true,
            url: true,
          },
          contractFile: {
            id: true,
            fileName: true,
            url: true,
          },
          businessLicenseFile: {
            id: true,
            fileName: true,
            url: true,
          },
          certFiles: {
            id: true,
            fileName: true,
            url: true,
          },
          hourlyRate: true,
          companyName: true,
          status: true,
          employmentType: true,
          id: true,
          name: true,
          email: true,
          phone: true,
          contactPerson: true,
          vendorTypes: true,
          startDate: true,
          endDate: true,
          notes: true,
          isRecurring: true,
          netsuiteVendorId: true,
          netsuiteVendorName: true,
          salesforceVendorId: true,
          activeVendorServices: {
            service: true,
            homeId: true,
            ratePeriod: true,
            rateInCents: true,
          },
          availabilities: {
            __args: { availabilityRange },
            date: true,
            times: {
              startTime: true,
              endTime: true,
            },
            kind: true,
          },
          activeVendorServicesRegionStats: {
            regionServices: {
              regionId: true,
              regionName: true,
              vendorHomes: {
                name: true,
                id: true,
              },
              regionHomesCount: true,
              vendorHomesToRegionHomesPercentage: true,
            },
          },
          ...(homeId
            ? {
                ownerPreferredExistsInServices: {
                  __args: { homeId },
                },
                ownerManagedExistsInServices: {
                  __args: { homeId },
                },
                billingFrequencyMostRecentInServices: {
                  __args: { homeId },
                },
              }
            : {}),
        },
      })
      .then(results => results?.findVendor)
  },
  async getVendorByName(term: string) {
    return arriereBackoffice.queries
      .vendors({
        __alias: 'searchVendorsByName',
        searchVendorsByName: {
          __args: { term },
          id: true,
          name: true,
        },
      })
      .then(results => results.searchVendorsByName)
      .catch(error => {
        Alert.Error((error as Error).message)
        return []
      })
  },
  async createVendorService(data: VendorServiceBodyWithAmountRule[]) {
    return arriereBackoffice.mutations
      .vendorService({
        __alias: 'createVendorRates',
        createVendorRates: {
          __args: { body: { vendorServiceBodies: data } },
        },
      })
      .then(results => {
        Alert.Success('Recurring Service successfully created.')
        return results.createVendorRates
      })
      .catch(error => {
        Alert.Error((error as Error).message)
        return false
      })
  },
  async getPropertyServicesAndVendors(homeId: string) {
    return arriereBackoffice.queries
      .vendorServices({
        __alias: 'propertyVendorServices',
        propertyVendorServices: {
          __args: { homeId },
          services: {
            vendorId: true,
            isActive: true,
            service: true,
            serviceFrequency: true,
            frequencyInterval: true,
            weekDays: true,
            vendorRateId: true,
            rateInCents: true,
            ratePeriod: true,
            payingParty: true,
            notes: true,
            isHousekeeping: true,
            billingFrequency: true,
            regionId: true,
            homeId: true,
            vendor: { id: true, name: true },
            daysOfService: true,
            autoAssign: true,
            effectiveRateInCents: true,
            amountRule: {
              baseServiceName: true,
              percentage: true,
              minimumRateInCents: true,
            },
          },
          area: true,
        },
      })
      .then(results => results.propertyVendorServices)
  },
  async updateVendorRecurring(vendorId: string, isRecurring: boolean) {
    return arriereBackoffice.mutations
      .vendors({
        __alias: 'propertyVendorServices',
        updateVisitFrequency: {
          __args: { vendorId, isRecurring },
        },
      })
      .then(results => results.updateVisitFrequency)
  },
  async updateVendorStatus(vendorRateId: string, isActive: boolean) {
    return arriereBackoffice.mutations
      .vendorService({
        __alias: 'propertyVendorServices',
        updateVendorRateStatus: {
          __args: { vendorRateId, isActive },
        },
      })
      .then(results => results.updateVendorRateStatus)
  },
  async updateVendorRates(vendorServiceBodies: UpdateVendorRateBody[]) {
    return arriereBackoffice.mutations
      .vendorService({
        __alias: 'updateVendorRates',
        updateVendorRates: {
          __args: { vendorServiceBodies },
        },
      })
      .then(results => results.updateVendorRates)
  },
  async getVendorRateHistory(vendorRateId: string) {
    return arriereBackoffice.queries
      .vendorServices({
        __alias: 'vendorRateHistory',
        vendorRateHistory: {
          __args: { vendorRateId },
          fieldName: true,
          originalValue: true,
          newValue: true,
          createdAt: true,
          userId: true,
          actionAuthor: true,
          source: true,
        },
      })
      .then(results => results.vendorRateHistory)
  },
  async getVendorsFromActiveService() {
    return arriereBackoffice.queries
      .vendorServices({
        __alias: 'getVendorsFromActiveService',
        vendorsFromActiveServices: {
          id: true,
          name: true,
        },
      })
      .then(results =>
        results.vendorsFromActiveServices?.map(vendor => ({
          label: vendor.name,
          value: vendor.id,
        })),
      )
  },
  async getHomeServices({
    showOffboarded,
    regionIds,
    homeIds,
    vendorIds,
    serviceNames,
    sortBy,
    sortOrder,
    page,
    size,
  }: GetHomeServicesProps) {
    return arriereBackoffice.queries
      .vendorServices({
        __alias: 'searchHomeServices',
        searchHomeServices: {
          __args: {
            withOffboarded: showOffboarded,
            regionIds,
            homeIds,
            vendorIds,
            serviceNames,
            sortBy,
            sortOrder,
            pagination: { page, size },
          },
          total: true,
          pages: true,
          results: {
            home: {
              id: true,
              name: true,
              area: true,
              region: {
                id: true,
                name: true,
              },
            },
            services: {
              service: true,
              serviceFrequency: true,
              effectiveRateInCents: true,
              ratePeriod: true,
              autoAssign: true,
              vendorRateId: true,
              vendor: {
                id: true,
                name: true,
              },
            },
          },
        },
      })
      .then(results => results.searchHomeServices)
  },
  getHomeOptions: memoize(
    async (name?: string, regionIds?: string[]) => {
      return arriereBackoffice.queries.homes
        .raw({
          __alias: 'getHomeOptions',
          __args: { name, regionIds, withOffboarded: true, pagination: { page: 1, size: 99999 } },
          results: {
            id: true,
            name: true,
            title: true,
            isAncestor: true,
            region: {
              name: true,
            },
          },
        })
        .then(({ data }) => {
          return data.results
        })
        .catch(error => {
          Alert.Error((error as Error).message)
          return []
        })
    },
    { maxAge: MAX_AGE_MEMOIZE, isPromise: true, isDeepEqual: true },
  ),
}

export const getPropertyNpsScore = async ({ homeId, start, end }: PropertiesReviewsParams) => {
  return arriereBackoffice.queries.home
    .settle({
      __alias: 'getPropertyNpsScore',
      __args: {
        homeId,
      },
      indicators: {
        customerExperience: {
          npsInfo: {
            __args: { from: start, to: end },
            score: true,
          },
        },
      },
    })
    .then(result => settledDataOrThrow({ result, queryName: 'getPropertyNpsScore' }))
}

export const getPropertyReviewsOverview = async ({
  homeId,
  start,
  end,
}: PropertiesReviewsParams) => {
  return arriereBackoffice.queries.home
    .settle({
      __alias: 'getPropertyReviewsOverview',
      __args: {
        homeId,
      },
      ratings: {
        __args: { period: { start, end } },
        ...ratingsOverviewFragment,
      },
      indicators: {
        customerExperience: {
          npsInfo: {
            __args: { from: start, to: end },
            ...npsRatingsOverviewFragment,
          },
        },
      },
    })
    .then(result => settledDataOrThrow({ result, queryName: 'getPropertyReviewsOverview' }))
}

export const homesListWithCalendarFragment = (from: string, to: string) => ({
  id: true,
  name: true,
  title: true,
  unitNumber: true,
  maxOccupancy: true,
  details: {
    onBoardingStatus: true,
  },
  lifecycle: {
    guestReadinessStatus: true,
  },
  sourceListed: {
    website: true,
  },
  calendar: {
    __args: {
      from,
      to,
    },
    date: true,
    status: true,
    rate: true,
    minStay: true,
    restrictions: {
      checkInAllowed: true,
      checkOutAllowed: true,
    },
  },
  bookings: {
    __args: {
      from,
      to,
      leadSources: [],
    },
    results: {
      id: true,
      reservationType: {
        name: true,
      },
      period: {
        start: true,
        end: true,
      },
      status: true,
    },
  },
})

export const getHomesListWithCalendar = async ({
  from,
  to,
  name,
  hotelId,
  hotelRoomTypeId,
  alias,
}: HomesWithCalendarInput & { alias?: string }) => {
  return arriereBackoffice.queries.homes.raw({
    __alias: alias ?? 'getHomesListWithCalendar',
    __args: { name, hotelId, hotelRoomTypeId },
    results: {
      thumbnail: {
        url: true,
      },
      region: {
        name: true,
      },
      isAncestor: true,
      isSuccessor: true,
      revenueModel: true,
      ...homesListWithCalendarFragment(from, to),
    },
  })
}

export const getHomeCustomerExperienceIndicators = async ({
  from,
  homeId,
  to,
}: HomeCustomerExperienceIndicators) => {
  return arriereBackoffice.queries.home
    .settle({
      __alias: 'getHomeCustomerExperienceIndicators',
      __args: {
        homeId,
      },
      ratings: {
        __args: { period: { start: from, end: to } },
        airBnb: {
          overall: {
            avgStars: true,
          },
        },
      },
      indicators: {
        customerExperience: {
          nps: { __args: { from, to } },
          positiveReviews: { __args: { from, to, source: LeadSource.airBnb } },
        },
      },
    })
    .then(async result => {
      const data = settledDataOrThrow({ result, queryName: 'getHomeCustomerExperienceIndicators' })

      const tripIssuesData = await getHomesAirbnbTripIssues({
        homeIds: [homeId],
        regionIds: [],
      })?.then(data => data.response.find(result => result.id === homeId))

      return {
        ...data.indicators.customerExperience,
        avgRating: data.ratings?.airBnb?.overall?.avgStars,
        ...(tripIssuesData || {}),
      }
    })
}

export const verifyPropertyInfo = ({ homeId, verifications }: VerifyPropertyInfoProps) => {
  return arriereBackoffice.mutations.home({
    verifyItems: {
      __args: { homeId, verifications },
    },
  })
}

export const getBookingsWithOtaIssues = (homeIds: string[]) => {
  return arriereBackoffice.queries.bookingsWithOtaIssues({
    __alias: 'getBookingsWithOtaIssues',
    __args: { homeIds },
    booking: {
      hash: true,
      cancellationInfo: {
        source: true,
        createDate: true,
      },
      otaBookingInfo: {
        otaBookingId: true,
      },
      guestName: true,
      guests: true,
      period: {
        end: true,
        start: true,
      },
      status: true,
      reviews: {
        categoryReviews: {
          category: true,
          rating: true,
          comment: true,
          tags: true,
        },
        privateFeedback: true,
        response: true,
      },
      id: true,
    },
    bookingChannelId: true,
    details: true,
    issues: {
      bookingChannelId: true,
      cancellation: true,
      csViolations: {
        category: true,
        tag: true,
      },
      listingId: true,
      ota: true,
      reviewIssues: {
        lowCategoryRatings: {
          category: true,
          rating: true,
        },
        lowOverallRating: true,
        negativeCategoryTags: {
          category: true,
          tag: true,
        },
        otaReviewId: true,
      },
    },
  })
}

export const getOwnerRestrictions = (homeId: UUID) => {
  return arriereBackoffice.queries
    .home({
      __alias: 'getOwnerRestrictions',
      __args: { homeId },
      ownerRequiredRestrictions: {
        id: true,
        rules: {
          createdAt: true,
          createdBy: {
            kind: true,
            name: true,
          },
          floorRate: true,
          id: true,
          includeWeekdays: true,
          includeWeekends: true,
          isActive: true,
          maxNights: true,
          minNights: true,
          name: true,
          note: true,
          period: {
            end: true,
            start: true,
          },
          updatedAt: true,
          updatedBy: {
            kind: true,
            name: true,
          },
        },
      },
    })
    .then(res => res.ownerRequiredRestrictions)
}

export const getOwnerRestrictionsChanges = (homeId: UUID) => {
  return arriereBackoffice.queries
    .home({
      __alias: 'getOwnerRestrictionsChanges',
      __args: { homeId },
      ownerRequiredRestrictionChanges: {
        author: {
          name: true,
        },
        createdAt: true,
        changes: {
          floorRate: {
            from: true,
            to: true,
          },
          includeWeekdays: {
            from: true,
            to: true,
          },
          includeWeekends: {
            from: true,
            to: true,
          },
          isActive: {
            from: true,
            to: true,
          },
          maxNights: {
            from: true,
            to: true,
          },
          minNights: {
            from: true,
            to: true,
          },
          name: {
            from: true,
            to: true,
          },
          note: {
            from: true,
            to: true,
          },
          period: {
            from: {
              end: true,
              start: true,
            },
            to: {
              end: true,
              start: true,
            },
          },
        },
        data: {
          id: true,
          data: {
            name: true,
            floorRate: true,
            includeWeekdays: true,
            includeWeekends: true,
            isActive: true,
            maxNights: true,
            minNights: true,
            note: true,
            period: {
              end: true,
              start: true,
            },
          },
        },
        kind: true,
      },
    })
    .then(res => res.ownerRequiredRestrictionChanges)
}

export const getDiscountRules = async () => {
  return arriereBackoffice.queries.discountRules
    .settle({
      __alias: 'getDiscountRules',
      kind: true,
      supportedAgencies: true,
      supportedThresholds: true,
      tooltip: true,
      agencyThresholds: {
        agency: true,
        thresholds: true,
      },
    })
    .then(result => settledDataOrThrow({ result, queryName: 'getDiscountRules' }))
}

export const getHomeDiscountSettings = async (homeId: UUID) => {
  return arriereBackoffice.queries
    .home({
      __alias: 'getHomeDiscountSettings',
      __args: { homeId },
      agencyDiscountSettings: {
        agency: true,
        settings: {
          discount: true,
          enabled: true,
          ruleKind: true,
          markup: true,
          threshold: true,
          updatedAt: true,
          updatedBy: {
            name: true,
          },
          externalStatus: {
            kind: true,
            status: true,
            reason: true,
            updatedAt: true,
            updatedBy: {
              kind: true,
              name: true,
            },
          },
        },
      },
    })
    .then(res => res?.agencyDiscountSettings)
}

// Mutations
export const updateHomeData = async ({
  homeId,
  title,
  name,
  shortDescription,
  description,
  address,
  maxOccupancy,
  maxAdults,
  bathroomsCount,
  halfBathroomsCount,
  coordinates,
  unitNumber,
  area,
}: UpdateHomeDataParams) => {
  return arriereBackoffice.mutations.updateHomeData({
    __alias: 'updateHomeBasicInfo',
    __args: {
      homeId,
      homeData: {
        title,
        name,
        shortDescription,
        description,
        maxOccupancy,
        maxAdults,
        bathroomsCount,
        halfBathroomsCount,
        unitNumber,
        area,
      },
      addressData: {
        ...address,
        coordinates,
      },
    },
    id: true,
    latitude: true,
    longitude: true,
  })
}

export interface SyncPropertyDistributionParams extends SyncDistributionParams {
  homeId: string
}

export interface GetHomeServicesProps {
  showOffboarded: boolean
  regionIds?: string[]
  homeIds?: string[]
  vendorIds?: string[]
  serviceNames?: ServiceNameType[]
  sortBy?: PropertyBasedServicesSorting
  sortOrder?: SortOrder
  page: number
  size: number
}

export const syncAirbnbDistribution = ({
  homeId,
  period,
  pushAmenities,
  pushCalendar,
  pushContent,
  pushHomePhotos,
  pushPricing,
  pushRooms,
  pushSeasonalDiscounts,
  pushTaxFees,
}: SyncPropertyDistributionParams) => {
  return arriereBackoffice.mutations.distribution({
    __alias: 'syncAirbnbDistribution',
    asyncPushToAirbnb: {
      __args: {
        params: {
          homeIds: [homeId],
          period,
          pushAmenities,
          pushCalendar,
          pushContent,
          pushHomePhotos,
          pushPricing,
          pushRooms,
          pushSeasonalDiscounts,
          pushTaxFees,
        },
      },
      status: true,
    },
  })
}

export const syncRentalsUnitedDistribution = ({
  homeId,
  period,
  pushAmenities,
  pushCalendar,
  pushContent,
  pushHomePhotos,
  pushPricing,
  pushRooms,
  pushTaxFees,
}: SyncPropertyDistributionParams) => {
  return arriereBackoffice.mutations.distribution({
    __alias: 'syncRentalsUnitedDistribution',
    asyncPushToRentalsUnited: {
      __args: {
        params: {
          homeIds: [homeId],
          period,
          pushAmenities,
          pushCalendar,
          pushContent,
          pushHomePhotos,
          pushPricing,
          pushRooms,
          pushTaxFees,
        },
      },
      status: true,
    },
  })
}

export const syncVrboDistribution = ({
  homeId,
  pushCalendar,
  pushPricing,
  pushSeasonalDiscounts,
  pushTaxFees,
}: SyncPropertyDistributionParams) => {
  return arriereBackoffice.mutations.distribution({
    __alias: 'syncVrboDistribution',
    asyncPushToVrbo: {
      __args: {
        params: {
          homeIds: [homeId],
          pushCalendar,
          pushPricing,
          pushSeasonalDiscounts,
          pushTaxFees,
        },
      },
      status: true,
    },
  })
}

export const syncYieldPlanetDistribution = ({
  homeId,
  pushCalendar,
  pushPricing,
}: SyncPropertyDistributionParams) => {
  return arriereBackoffice.mutations.distribution({
    __alias: 'syncYieldPlanetDistribution',
    asyncPushToYieldPlanet: {
      __args: {
        params: {
          homeIds: [homeId],
          pushCalendar,
          pushPricing,
        },
      },
      homeId: true,
      status: true,
    },
  })
}

export const getPropertyReviewsSourceStats = async ({
  homeId,
  page,
  paginationSize,
  from,
  to,
  terms,
  minRating,
  maxRating,
  categories,
}: GetPropertyReviewsProps) => {
  return arriereBackoffice.queries
    .home({
      __alias: 'propertyReviewsSourceStats',
      __args: {
        homeId,
      },
      reviewsByBookingSources: {
        __args: {
          pagination: { page, size: paginationSize },
          periodKind: ReviewPeriodKind.reviewCreationDate,
          period: { start: from, end: to },
          minRating,
          maxRating,
          terms,
          categories,
        },
        sourceFacet: {
          source: true,
          count: true,
        },
        total: true,
      },
    })
    .then(results => results.reviewsByBookingSources)
}

export const updateOwnerRestrictions = (input: SaveOwnerRestrictionFormProps) => {
  return arriereBackoffice.mutations.home({
    __alias: 'updateOwnerRestrictions',
    updateHomeOwnerRequiredRestrictions: {
      __args: {
        input,
      },
    },
  })
}

export const updateHomeAgencyDiscountSettings = ({
  homeId,
  input,
}: UpdateHomeDiscountSettingProps) => {
  return arriereBackoffice.mutations.home({
    __alias: 'updateHomeAgencyDiscountSettings',
    updateHomeAgencyDiscountSettings: {
      __args: {
        homeId,
        input,
      },
    },
  })
}

interface AllowAirbnbLockedAttributeParams {
  listingId: string
  attribute: string
  reason: string
}

interface DisallowAirbnbLockedAttributeParams {
  listingId: string
  attribute: string
}

export const getAirbnbAllowedLockedAttributes = async (listingId: string) => {
  const { data, errors } = await arriereBackoffice.queries.airbnbDistribution.raw({
    __alias: 'getAirbnbAllowedLockedAttributes',
    listings: {
      allowedLockedAttributes: {
        __args: { listingId },
        attribute: true,
        reason: true,
        createdAt: true,
        createdBy: {
          name: true,
        },
      },
    },
  })

  if (errors?.length) {
    throw new Error(errors[errors.length - 1]?.message)
  }

  return data?.listings?.allowedLockedAttributes ?? []
}

export const allowAirbnbLockedAttribute = async ({
  listingId,
  attribute,
  reason,
}: AllowAirbnbLockedAttributeParams) => {
  const { data, errors } = await arriereBackoffice.mutations.airbnbDistribution.raw({
    __alias: 'allowAirbnbLockedAttribute',
    listings: {
      allowLockedAttribute: {
        __args: { listingId, attribute, reason },
      },
    },
  })

  if (errors?.length) {
    throw new Error(errors[errors.length - 1]?.message)
  }

  return Boolean(data?.listings?.allowLockedAttribute)
}

export const disallowAirbnbLockedAttribute = async ({
  listingId,
  attribute,
}: DisallowAirbnbLockedAttributeParams) => {
  const { data, errors } = await arriereBackoffice.mutations.airbnbDistribution.raw({
    __alias: 'disallowAirbnbLockedAttribute',
    listings: {
      disallowLockedAttribute: {
        __args: { listingId, attribute },
      },
    },
  })

  if (errors?.length) {
    throw new Error(errors[errors.length - 1]?.message)
  }

  return Boolean(data?.listings?.disallowLockedAttribute)
}
